// src/pages/ConsultationNew.js
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import jsPDF from "jspdf";
import DashboardLayout from "../../../layouts/DashboardLayout";
import TranscriptionConsentNotice from "../../../components/consultation/TranscriptionConsentNotice";
import SessionTranscript from "../../../components/consultation/SessionTranscript";
import SessionFeedbackCard from "../../../components/consultation/SessionFeedbackCard";
import { Card, CardContent } from "../../../components/ui/CCard";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Label } from "../../../components/ui/Label";
import { Textarea } from "../../../components/ui/Textarea";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import api from "../../../api";
import PatientTakeawayPreview from "../../../components/consultation/PatientTakeawayPreview";
import { ownSummaryText, MIN_OWN_SUMMARY_CHARS } from "../../../utils/patientSummary";
import CognitiveModelReconstruction from "../../../components/CognitiveModelReconstruction";
import CognitiveModelComparison from "../../../components/CognitiveModelComparison";
import { pdf } from '@react-pdf/renderer';
import ConsultationPDFDocument from "../../../components/ConsultationPDFDocument";
import ManualReviewBadge from "../../../components/ui/ManualReviewBadge";
import ConfidenceIndicator from "../../../components/ui/ConfidenceIndicator";
import {
  MessageSquare, RefreshCw, FileEdit, Download, ChevronDown, FileText, UploadCloud, Loader2, User, Trash2,
  Brain, Activity, Sparkles, Printer, Wand2, AlertTriangle, ClipboardList, Pill, HeartHandshake, BarChart3, PenLine, Save, Send, BellRing, Video, Target, BookOpenCheck, CheckCircle2
} from "lucide-react";
import { jwtDecode } from "jwt-decode";
import VideoCallModal from "../appointment/VideoCallModal";
import HistoryModal from "../../../components/consultation/HistoryModal";
import ConsultationSidePanel from "../../../components/consultation/ConsultationSidePanel";
import PrescriptionTable from "../../../components/consultation/PrescriptionTable";
import TestsTable from "../../../components/consultation/TestsTable";
import FollowUpTable from "../../../components/consultation/FollowUpTable";
import ClinicalTypeaheadTextarea from "../../../components/consultation/ClinicalTypeaheadTextarea";
import SessionInsightsPanel from "../../../components/consultation/SessionInsightsPanel";
import RiskAssessmentSection, { emptyRisk, riskStatus } from "../../../components/consultation/RiskAssessmentSection";
import CrisisButton from "../../../components/consultation/CrisisButton";
import TreatmentPlanPanel from "../../../components/clinical/TreatmentPlanPanel";
import HomeworkPanel from "../../../components/clinical/HomeworkPanel";
import { useSessionAssist, SessionDraftBox } from "../../../components/consultation/SessionDrafts";
import PreviousSessionCarryover from "../../../components/consultation/PreviousSessionCarryover";
import FormulationPanel from "../../../components/clinical/FormulationPanel";
import AskSupervisionModal from "../../../components/clinical/AskSupervisionModal";
import NoteTemplateSections, { NoteTemplatePicker, NoteFormatPrompt, useSessionNoteFormat, rememberSessionNoteFormat, noteFormatNeeded } from "../../../components/consultation/NoteTemplateSection";
import NoteSigningPanel from "../../../components/consultation/NoteSigningPanel";
import useFeatures from "../../../hooks/useFeatures";
import { printMedicalPrescription, getPrescriptionHeaderSettings } from "../../../utils/prescriptionPrinter";


const SHOW_SESSION_DETAILS = false; // Date/Time/Duration panel hidden per product request

export default function ConsultationEdit() {
  const location = useLocation();
  const logoDataRef = useRef(null);

  // inside ConsultationNew
  const [consultationId, setConsultationId] = useState(null); // track if editing
  const features = useFeatures();
  const [riskData, setRiskData] = useState(emptyRisk());
  const [riskSaved, setRiskSaved] = useState(JSON.stringify(emptyRisk()));
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [noteLocked, setNoteLocked] = useState(false);   // signed note (NoteSigningPanel)
  useEffect(() => {
    if (features.has("note_templates")) api.getNoteTemplates().then((r) => setNoteTemplates(r.data || [])).catch(() => setNoteTemplates([]));
  }, [features.ready]); // eslint-disable-line
  const [appointmentId, setAppointmentId] = useState(null); // for the Messages card
  const [meetingInfo, setMeetingInfo] = useState(null); // appointment fields needed for Join Meeting
  const [videoCallOpen, setVideoCallOpen] = useState(false);
  const [askSupervision, setAskSupervision] = useState(false);
  // "side" while the call is docked on the right - the page makes room for it.
  const [videoLayout, setVideoLayout] = useState(null);
  // JSON snapshot of the last-saved (or just-loaded) payload shape - the
  // Save/Complete button is only enabled once the live form actually
  // differs from this, and the label itself reflects whether this
  // consultation has already been completed at least once before.
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [patientMessageDraft, setPatientMessageDraft] = useState("");
  const [generatingPatientMessage, setGeneratingPatientMessage] = useState(false);
  const [sendingPatientMessage, setSendingPatientMessage] = useState(false);
  const [patientMessageToast, setPatientMessageToast] = useState(null);
  const [reports, setReports] = useState([]);
  const [uploadingReports, setUploadingReports] = useState(false);
  const [reportName, setReportName] = useState("");
  const [linkedTestName, setLinkedTestName] = useState("");
  const [transcript, setTranscript] = useState(null);
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [hasCallTranscript, setHasCallTranscript] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState("");
  const [conversationEntries, setConversationEntries] = useState([]);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [patientDetails, setPatientDetails] = useState(null);
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [generatingRx, setGeneratingRx] = useState(false);

  const canDoctorPrescribeMedicine = (doc) => {
    if (!doc) return true;
    if (doc.canPrescribeMedicine !== undefined && doc.canPrescribeMedicine !== null) {
      return Boolean(doc.canPrescribeMedicine);
    }
    const spec = (doc.specialized || doc.specialization || "").toLowerCase();
    const nonPrescribing = ["psychologist", "psychotherapy", "counselor", "counsellor", "nutrition", "dietetics"];
    if (nonPrescribing.some(kw => spec.includes(kw))) {
      return false;
    }
    return true;
  };
  const getSavedPrintMode = () => {
    try {
      const saved = localStorage.getItem("default_prescription_print_mode");
      if (saved) return saved;
      const cfg = getPrescriptionHeaderSettings();
      return cfg.default_hide_header ? "preprinted" : "full";
    } catch (e) {
      return "full";
    }
  };

  const [history, setHistory] = useState([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [isPrintMenuOpen, setIsPrintMenuOpen] = useState(false);
  const [selectedPrintMode, setSelectedPrintMode] = useState(getSavedPrintMode);
  // Visit Summary printouts leave the diagnosis off unless asked for each time.
  const [summaryIncludeDiagnosis, setSummaryIncludeDiagnosis] = useState(false);
  const [prescriptionHeaderConfig, setPrescriptionHeaderConfig] = useState(null);
  const [consultationData, setConsultationData] = useState({
    // Not user-selectable - fixed at creation time from the doctor's
    // specialization; this placeholder is immediately overwritten by the
    // saved record's own value once it loads (see fetch effect below).
    consultation_type: "mental_health",
    sessiondate: "",
    sessiontime: "",
    sessionduration: "",
    mood: "",
    mainthemes: "",
    resistanceorengagement: "",
    interventions: "",
    interventionseffectiveness: "",
    updatedhypothesis: "",
    sessionoutcome: "",
    nextplan: "",
    selfreflection: "",
    what_went_well: "",
    summary: "",
    self_rating: null,
    gp_symptoms: "",
    gp_vitals: "",
    gp_diagnosis: "",
    gp_prescription: "",
    gp_advice: "",
    gp_tests: "",
    follow_up_items: [],
  });

  // Tests the AI most recently suggested via "Generate with AI" - kept only
  // in memory (not persisted) so we can flag on-screen if a test it marked
  // required_before_medicine hasn't actually been added to gp_tests yet.
  const [aiSuggestedTests, setAiSuggestedTests] = useState([]);

  const [reflectionEffective, setReflectionEffective] = useState("");
  const [reflectionStuck, setReflectionStuck] = useState("");
  const [reflectionNextTime, setReflectionNextTime] = useState("");
  const [cognitiveReconstruction, setCognitiveReconstruction] = useState(null);
  const [reconstructionFormData, setReconstructionFormData] = useState(null);
  const [loadingReconstruction, setLoadingReconstruction] = useState(false);
  const [actualModel, setActualModel] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const isCustomPersona = useMemo(() => {
    try {
      if (!patientDetails?.virtual_profile) return false;
      const vp = typeof patientDetails.virtual_profile === "string"
        ? JSON.parse(patientDetails.virtual_profile)
        : patientDetails.virtual_profile;
      // Only check for is_custom_persona flag - cognitive_model exists in both random and custom personas
      return !!(vp?.is_custom_persona);
    } catch {
      return false;
    }
  }, [patientDetails]);

  const [isAdmin, setIsAdmin] = useState(false);
  const [isDoctor, setIsDoctor] = useState(false);
  const [isReceptionist, setIsReceptionist] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("auth");
    if (token) {
      try {
        const decoded = jwtDecode(JSON.parse(token).token);
        const role = (decoded.sub?.role || JSON.parse(token).role || "").toLowerCase();
        setIsAdmin(["admin", "ops", "operations"].includes(role));
        setIsDoctor(role === "doctor");
        // Front-desk/ops role: can only capture intake vitals & complaints
        // history and manage their own report uploads - everything else on
        // the consultation is clinical content, view-only for this role.
        // Enforced on the backend too (update_consultation strips any other
        // field a receptionist sends), this is just so the UI doesn't invite
        // edits that would be silently dropped.
        setIsReceptionist(role === "receptionist");
        setCurrentUserId(decoded.sub?.user_id ?? null);
      } catch (e) {
        console.error("Failed to decode token", e);
      }
    }
  }, []);


  const navigate = useNavigate();

  const { id } = useParams(); // check if we are in edit mode
  const handleOpenConversationModal = async () => {
    if (!id) {
      alert("Conversation history is available only after the consultation is created.");
      return;
    }
    setIsConversationModalOpen(true);
    setConversationLoading(true);
    setConversationError("");
    try {
      const res = await api.getConsultationConversation(id);
      setConversationEntries(res.data?.conversation || []);
    } catch (error) {
      console.error("Failed to fetch conversation", error);
      setConversationEntries([]);
      setConversationError("Unable to load conversation history.");
    } finally {
      setConversationLoading(false);
    }
  };

  const handleCloseConversationModal = () => {
    setIsConversationModalOpen(false);
    setConversationEntries([]);
    setConversationError("");
  };

  const handleGenerateSummary = async () => {
    if (!id) {
      alert("Consultation ID is required to generate summary.");
      return;
    }

    // Check if evaluation already exists (re-evaluation scenario)
    const hasExistingEvaluation = consultationData.summary && consultationData.summary.trim() !== "";

    if (hasExistingEvaluation) {
      // Show credit warning for re-evaluation
      if (!window.confirm(
        "⚠️ Re-evaluation Notice\n\n" +
        "This session already has an evaluation. Re-evaluating will:\n" +
        "• Consume 1 credit from your account\n" +
        "• Replace the existing evaluation\n\n" +
        "Do you want to proceed with re-evaluation?"
      )) {
        return; // User cancelled
      }
    } else {
      // Normal first-time evaluation confirmation
      if (!window.confirm("This will generate a summary based on the conversation history. This may take a few moments. Continue?")) {
        return;
      }
    }

    setGeneratingSummary(true);
    try {
      const res = await api.generateConsultationSummary(id, { analysis_mode: "audit_with_citations" });
      if (res.status === 200) {
        alert(res.data.message || "Summary generation started. Please refresh the page in a few moments to see the updated summary.");
        // Optionally refresh the consultation data after a delay
        setTimeout(async () => {
          try {
            const updatedRes = await api.getConsultation(id);
            if (updatedRes.data) {
              setConsultationData(prev => ({
                ...prev,
                summary: updatedRes.data.summary || prev.summary,
                what_went_well: updatedRes.data.what_went_well || prev.what_went_well,
                mainthemes: updatedRes.data.mainthemes || prev.mainthemes,
                mood: updatedRes.data.mood || prev.mood,
                resistanceorengagement: updatedRes.data.resistanceorengagement || prev.resistanceorengagement,
                interventions: updatedRes.data.interventions || prev.interventions,
                selfreflection: updatedRes.data.selfreflection || prev.selfreflection,
                nextplan: updatedRes.data.nextplan || prev.nextplan,
                self_rating: updatedRes.data.self_rating ?? prev.self_rating
              }));
            }
          } catch (refreshError) {
            console.error("Failed to refresh consultation data:", refreshError);
          }
        }, 5000); // Wait 5 seconds before trying to refresh
      } else {
        throw new Error(res.data?.error || "Failed to generate summary");
      }
    } catch (error) {
      console.error("Failed to generate summary:", error);
      alert(error.response?.data?.error || error.message || "Failed to generate summary. Please try again.");
    } finally {
      // setGeneratingSummary(false); // Do not turn off immediately, wait for polling to verify completion
    }
  };

  // No polling - user must manually refresh to see updates as per request
  // But we still track 'processing' status from initial load to disable button
  useEffect(() => {
    // This effect is intentionally empty of polling logic.
    // State is managed by initial load in the main useEffect below.
  }, []);


  const fetchReports = async (consultId) => {
    try {
      const res = await api.getConsultationReports(consultId);
      setReports(res.data || []);
    } catch (err) {
      console.error("Failed to fetch reports", err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !id) return;
    if (!reportName.trim() && !linkedTestName) {
      alert("Please enter a report name or select a test before uploading.");
      return;
    }

    setUploadingReports(true);
    const formData = new FormData();
    formData.append("file", file);
    // Fall back to the linked test's name so the report still gets a
    // sensible label even when the doctor picked a test instead of typing one.
    formData.append("name", reportName.trim() || linkedTestName);
    if (linkedTestName) {
      formData.append("linked_test_name", linkedTestName);
    }

    try {
      await api.uploadConsultationReport(id, formData);
      await fetchReports(id);
      alert("Report uploaded successfully!");
      setReportName(""); // reset report name
      setLinkedTestName(""); // reset linked test selection
    } catch (err) {
      console.error(err);
      alert("Failed to upload report. Check console.");
    } finally {
      setUploadingReports(false);
      e.target.value = null; // reset input
    }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const res = await api.getReportDownloadUrl(reportId);
      if (res.data && res.data.url) {
        window.open(res.data.url, "_blank");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to get download link");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report? This cannot be undone.")) return;
    try {
      await api.deleteReport(reportId);
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch (err) {
      console.error(err);
      alert("Failed to delete report.");
    }
  };

  const calculateAge = (dob, referenceDate) => {
    if (!dob) return "-";
    const dobDate = new Date(dob);
    const ref = referenceDate ? new Date(referenceDate) : new Date();
    let age = ref.getFullYear() - dobDate.getFullYear();
    const m = ref.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && ref.getDate() < dobDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : "-";
  };

  const formatAIPrescriptionForTable = (prescriptionItems) => {
    return (prescriptionItems || [])
      .filter((m) => m && m.medicine_name)
      .map((m) => {
        const name = [m.medicine_name, m.strength].filter(Boolean).join(" ").trim();
        const timing = m.timing || "After Food";
        const dose = m.dose || "1-0-1";
        const duration = m.duration || "";
        return [name, timing, dose, duration].join(" | ");
      })
      .join("\n");
  };

  const formatAIAdviceForTextarea = (adviceItems) => {
    return (adviceItems || []).filter(Boolean).map((a) => `• ${a}`).join("\n");
  };

  const formatAITestsForTable = (testItems) => {
    return (testItems || [])
      .filter((t) => t && t.test_name)
      .map((t, idx) => {
        const parts = [`${idx + 1}. ${t.test_name.trim()}`];
        if (t.reason && t.reason.trim()) parts.push(t.reason.trim());
        return parts.join(" | ");
      })
      .join("\n");
  };

  const handleGenerateAIPrescription = async () => {
    if (!canDoctorPrescribeMedicine(doctorDetails)) {
      alert("Medicine prescription is restricted for this doctor's specialization (e.g. Clinical Psychologist cannot prescribe medicine).");
      return;
    }
    if (!consultationData.gp_diagnosis || !consultationData.gp_diagnosis.trim()) {
      alert("Please enter a Clinical Diagnosis / Assessment before generating an AI prescription.");
      return;
    }
    const specialization = doctorDetails?.specialized;
    if (!specialization) {
      alert("Doctor specialization not found. Please set a specialization on the doctor's profile before using AI generation.");
      return;
    }
    if (
      (consultationData.gp_prescription || consultationData.gp_advice) &&
      !window.confirm("This will replace the current prescription and advice with AI-generated content. Continue?")
    ) {
      return;
    }

    setGeneratingRx(true);
    try {
      const age = calculateAge(patientDetails?.dob);
      const res = await api.generateDoctorPrescription({
        specialization,
        complaints_history: consultationData.gp_symptoms,
        vitals_examination: consultationData.gp_vitals,
        diagnosis: consultationData.gp_diagnosis,
        patient_age: age === "-" ? null : age,
        patient_sex: patientDetails?.gender,
      });
      setConsultationData((prev) => ({
        ...prev,
        gp_prescription: formatAIPrescriptionForTable(res.data?.prescription),
        gp_advice: formatAIAdviceForTextarea(res.data?.advice),
        gp_tests: formatAITestsForTable(res.data?.tests),
      }));
      setAiSuggestedTests(res.data?.tests || []);
    } catch (err) {
      console.error("AI prescription generation failed:", err);
      alert(err.response?.data?.error || "Failed to generate AI prescription. Please try again.");
    } finally {
      setGeneratingRx(false);
    }
  };

  // Rewrites the current "Shared with Patient" advice into a warm,
  // second-person draft - never touches gp_advice itself, just prefills a
  // separate preview the doctor can edit before sending.
  const handleGeneratePatientMessage = async () => {
    if (!consultationData.gp_advice || !consultationData.gp_advice.trim()) return;
    setGeneratingPatientMessage(true);
    try {
      const res = await api.rewriteAdviceForPatient({
        advice: consultationData.gp_advice,
        patient_name: patientDetails?.name,
        doctor_name: doctorDetails?.name,
      });
      setPatientMessageDraft(res.data?.patient_summary || "");
    } catch (err) {
      console.error("Failed to generate patient message", err);
      setPatientMessageToast({ type: "error", text: "Failed to generate message. Please try again." });
      setTimeout(() => setPatientMessageToast(null), 5000);
    } finally {
      setGeneratingPatientMessage(false);
    }
  };

  const handleSendPatientMessage = async () => {
    if (!patientMessageDraft.trim() || !appointmentId) return;
    setSendingPatientMessage(true);
    try {
      await api.sendAppointmentMessage(appointmentId, { message: patientMessageDraft.trim() });
      setPatientMessageDraft("");
      setPatientMessageToast({ type: "success", text: "Message sent to patient." });
      setTimeout(() => setPatientMessageToast(null), 5000);
    } catch (err) {
      console.error("Failed to send patient message", err);
      setPatientMessageToast({ type: "error", text: "Failed to send message. Please try again." });
      setTimeout(() => setPatientMessageToast(null), 5000);
    } finally {
      setSendingPatientMessage(false);
    }
  };

  const formatDateTime = () => {
    const sessionDate = consultationData.sessiondate;
    const sessionTime = consultationData.sessiontime;
    if (!sessionDate && !sessionTime) {
      return { displayDate: "-", displayTime: "-" };
    }
    const dateObj = new Date(
      `${sessionDate || new Date().toISOString().slice(0, 10)}T${sessionTime || "00:00:00"
      }`
    );
    const displayDate = dateObj.toLocaleDateString();
    const displayTime = dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return { displayDate, displayTime };
  };

  const loadLogoDataUrl = async () => {
    if (logoDataRef.current) {
      return logoDataRef.current;
    }
    const logoSrc = `${process.env.PUBLIC_URL || ""}/media/img/brain_black.png`;
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => resolve(null);
        img.src = logoSrc;
      });
      logoDataRef.current = dataUrl;
      return dataUrl;
    } catch (error) {
      console.warn("Unable to load logo image", error);
      return null;
    }
  };

  const handleDownloadConversationPdf = async (mode = 'full') => {
    if (!id) {
      alert("Conversation history is available only after the consultation is created.");
      return;
    }
    try {
      setConversationLoading(true);

      // 🔹 Optimization: Determine exactly what we need based on download mode
      const needsBenchmarks = mode === 'full' || mode === 'report_no_transcript';
      const needsFullTranscript = mode === 'full';
      const needsConversation = mode !== 'summary_only';

      // Build API calls dynamically
      const apiCalls = [
        api.getCognitiveReconstruction(id).catch(() => ({ data: null }))
      ];

      if (needsConversation) {
        apiCalls.push(api.getConsultationConversation(id, { include_transcript: needsFullTranscript }).catch(() => ({ data: { conversation: [] } })));
      } else {
        apiCalls.push(Promise.resolve({ data: { conversation: [] } }));
      }

      if (needsBenchmarks) {
        apiCalls.push(api.getConsultationBenchmark(id, { include_transcript: needsFullTranscript }).catch((err) => {
          console.error("[ConsultationEdit] Benchmark API Failed:", err);
          return {
            data: {
              benchmark: [],
              thresholds: {
                confidence_threshold: 0.35,
                confidence_playbook_threshold: 0.3,
                distance_threshold: 0.85,
                distance_playbook_threshold: 0.9
              }
            }
          };
        }));
      } else {
        apiCalls.push(Promise.resolve({ data: { benchmark: [], thresholds: {} } }));
      }

      const [reconstructionRes, conversationRes, benchmarkRes] = await Promise.all(apiCalls);

      const entries = conversationRes.data?.conversation || [];
      const reconstruction = reconstructionRes.data || null;
      const benchmarkData = benchmarkRes.data?.benchmark || [];
      const benchmarkThresholds = benchmarkRes.data?.thresholds || {
        confidence_threshold: 0.35,
        confidence_playbook_threshold: 0.3,
        distance_threshold: 0.85,
        distance_playbook_threshold: 0.9
      };

      // console.log("[ConsultationEdit] Benchmark Data Received:", {
      //   count: benchmarkData.length,
      //   data: benchmarkData,
      //   thresholds: benchmarkThresholds
      // });

      // Use the new PDF generator component
      // Use the new React PDF generator
      const blob = await pdf(
        <ConsultationPDFDocument
          consultationData={consultationData}
          patientDetails={patientDetails}
          doctorDetails={doctorDetails}
          conversationEntries={entries}
          cognitiveReconstruction={reconstruction}
          benchmarkData={benchmarkData}
          benchmarkThresholds={benchmarkThresholds}
          consultationId={id}
          downloadMode={mode}
          logoDataUrl={await loadLogoDataUrl()}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Consultation_Report_${mode}_${id || "draft"}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Failed to download conversation PDF", error);
      alert("Unable to download conversation history. Please try again.");
    } finally {
      setConversationLoading(false);
    }
  };


  const handleCopyTranscript = async () => {
    if (!conversationEntries || conversationEntries.length === 0) return;
    try {
      const textToCopy = conversationEntries.map(entry => {
        let text = "";
        if (entry.patient) text += `Patient:** ${entry.patient}\n\n`;
        if (entry.doctor) text += `Doctor:** ${entry.doctor}\n\n`;
        return text.trim();
      }).join('\n\n');

      await navigator.clipboard.writeText(textToCopy);
      alert("Transcript copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy transcript", err);
      alert("Failed to copy transcript.");
    }
  };

  const handleChange = (e) => {
    setConsultationData({ ...consultationData, [e.target.name]: e.target.value });
  };


  // 🔹 Save (Create or Update)
  const isMeetWindowOpen = () => {
    if (!meetingInfo?.date || !meetingInfo?.time) return false;
    const start = new Date(`${meetingInfo.date}T${meetingInfo.time}`);
    if (isNaN(start.getTime())) return false;
    const minutesUntilStart = (start - new Date()) / 60000;
    const end = new Date(start.getTime() + (meetingInfo.duration_minutes || 30) * 60000);
    return minutesUntilStart <= 15 && new Date() < end;
  };
  const canJoinMeeting =
    (meetingInfo?.appttype || "").toLowerCase() === "online" &&
    Boolean(meetingInfo?.meeting_room_name) &&
    consultationData?.status !== "completed" &&
    isMeetWindowOpen();

  // Same shape as the save payload below - used both to build it and to
  // snapshot "what's currently saved" so the Save/Complete button can tell
  // whether the live form actually differs from that.
  const buildSavePayload = (markCompleted) => {
    const payload = {
      ...consultationData,
      selfreflection: JSON.stringify({
        effective: reflectionEffective,
        stuck: reflectionStuck,
        next_time: reflectionNextTime
      }),
      transcript,
    };
    if (markCompleted) {
      payload.status = "completed";
    }
    return payload;
  };
  useEffect(() => {
    if (!consultationId || !features.has("risk")) return;
    api.getConsultationRisk(consultationId).then((r) => {
      const a = r.data;
      if (!a) return;
      const loaded = { ...emptyRisk(), ...Object.fromEntries(Object.keys(emptyRisk()).map((k) => [k, a[k] || ""])) };
      setRiskData(loaded);
      setRiskSaved(JSON.stringify(loaded));
    }).catch(() => {});
  }, [consultationId, features.ready]); // eslint-disable-line

  const riskDirty = riskStatus(riskData) === "complete" && JSON.stringify(riskData) !== riskSaved;
  const isDirty = savedSnapshot !== null && (JSON.stringify(buildSavePayload(false)) !== savedSnapshot || riskDirty);

  const handleSave = async (showNotification = true, markCompleted = false) => {
    // Like the risk assessment: a mental-health note needs a format before it's
    // completed, when the clinic has no default and none was picked.
    if (markCompleted && await noteFormatNeeded(appointmentId, consultationData.template_code)) {
      setAskNoteFormat(true);
      return false;
    }
    try {
      const payload = buildSavePayload(markCompleted);
      const snapshot = JSON.stringify(payload);
      if (showRisk) {
        const rs = riskStatus(riskData);
        if (rs === "complete") payload.risk_assessment = riskData;
        else if (rs === "incomplete" && markCompleted) {
          alert("Finish the risk assessment (Clinical note tab) before completing the consultation, or clear it.");
          return false;
        }
      }

      let res;
      if (consultationId) {
        res = await api.updateConsultation(consultationId, payload);
        if (markCompleted) setConsultationData((prev) => ({ ...prev, status: "completed" }));
        setSavedSnapshot(snapshot);
        if (payload.risk_assessment) setRiskSaved(JSON.stringify(riskData));

        // Save cognitive reconstruction if data exists and patient is not custom persona
        if (!isCustomPersona && reconstructionFormData) {
          const hasContent = Object.values(reconstructionFormData).some(arr => arr.length > 0);
          if (hasContent) {
            try {
              await api.saveCognitiveReconstruction(consultationId, reconstructionFormData);
            } catch (reconErr) {
              console.error("Error saving reconstruction:", reconErr);
              // Don't block the main save if reconstruction fails
            }
          }
        }

        if (showNotification) alert("Consultation updated successfully!");
      } else {
        res = await api.createConsultation(payload);
        setConsultationId(res.data.id);
        if (markCompleted) setConsultationData((prev) => ({ ...prev, status: "completed" }));
        setSavedSnapshot(snapshot);
        if (payload.risk_assessment) setRiskSaved(JSON.stringify(riskData));

        // Save cognitive reconstruction if data exists and patient is not custom persona
        if (!isCustomPersona && reconstructionFormData) {
          const hasContent = Object.values(reconstructionFormData).some(arr => arr.length > 0);
          if (hasContent) {
            try {
              await api.saveCognitiveReconstruction(res.data.id, reconstructionFormData);
            } catch (reconErr) {
              console.error("Error saving reconstruction:", reconErr);
              // Don't block the main save if reconstruction fails
            }
          }
        }

        if (showNotification) {
          alert("Consultation created successfully!");
          navigate(`/consultations/${res.data.id}/edit`); // redirect to edit mode
        }
      }
      return res?.data;
    } catch (err) {
      console.error("Save consultation error:", err);
      if (showNotification) alert(err?.response?.data?.error || "Failed to save consultation. Please try again.");
      return false;
    }
  };

  // 🔹 Fetch existing consultation if editing
  // 🔹 Fetch existing consultation if editing
  // Ref for core data fetch
  const isFetchingCoreRef = useRef(false);

  // 1. Core Data Fetch (Consultation, Patient, Actual Model)
  useEffect(() => {
    if (!id) return;

    // Prevent duplicate fetches in StrictMode/Rapid updates
    if (isFetchingCoreRef.current) return;
    isFetchingCoreRef.current = true;

    const controller = new AbortController();

    (async () => {
      try {
        // A. Fetch Consultation
        const res = await api.getConsultation(id, { signal: controller.signal });
        const data = res.data;

        if (controller.signal.aborted) return;
        await fetchReports(id);

        setConsultationId(data.id);
        setAppointmentId(data.appointment_id || null);
        // Appointment-derived fields (meeting_room_name/duration_minutes/
        // date/time/appttype) live on AppointmentModel, not this
        // consultation - get_consultation() injects them onto the
        // response (see session/consultation.py) since this page only
        // ever gets a consultation id, never the full appointment object
        // the way ConsultationNew.js does via navigation state.
        setMeetingInfo(
          data.appointment_id
            ? {
                id: data.appointment_id,
                appttype: data.appointment_appttype,
                date: data.appointment_date,
                time: data.appointment_time,
                meeting_room_name: data.meeting_room_name,
                duration_minutes: data.duration_minutes,
              }
            : null
        );
        const loadedConsultationData = {
          status: data.status || "in_progress",
          sessiondate: data.sessiondate || "",
          sessiontime: data.sessiontime || "",
          sessionduration: data.sessionduration || "",
          mood: data.mood || "",
          mainthemes: data.mainthemes || "",
          resistanceorengagement: data.resistanceorengagement || "",
          interventions: data.interventions || "",
          interventionseffectiveness: data.interventionseffectiveness || "",
          updatedhypothesis: data.updatedhypothesis || "",
          sessionoutcome: data.sessionoutcome || "",
          nextplan: data.nextplan || "",
          selfreflection: data.selfreflection || "",
          what_went_well: data.what_went_well || "",
          summary: data.summary || "",
          self_rating: data.self_rating ?? null,
          supervisor_evaluation: data.supervisor_evaluation || null,
          summary_generation_status: data.summary_generation_status || 'not_started',
          summary_generation_retry_count: data.summary_generation_retry_count ?? 0,
          supervisor_brief: data.supervisor_brief || null,
          review_comment: data.review_comment || "",
          review_status: data.review_status || "pending",
          reviewed_on: data.reviewed_on || null,
          review_required: data.review_required || false,
          // Transfer Case & Homework context (injected by backend from previous session)
          is_transfer_case: data.is_transfer_case || false,
          previous_doctor_name: data.previous_doctor_name || null,
          previous_homework_assigned: data.previous_homework_assigned || false,
          previous_homework_text: data.previous_homework_text || null,
          consultation_type: data.consultation_type || "mental_health",
          template_code: data.template_code || "default",
          template_values: data.template_values || null,
          gp_symptoms: data.gp_symptoms || "",
          gp_vitals: data.gp_vitals || "",
          gp_diagnosis: data.gp_diagnosis || "",
          gp_prescription: data.gp_prescription || "",
          gp_advice: data.gp_advice || "",
          gp_tests: data.gp_tests || "",
          follow_up_items: data.follow_up_items || [],
        };
        setConsultationData(loadedConsultationData);

        // 🔹 Parse Self-Reflection JSON if present
        let loadedEffective = "", loadedStuck = "", loadedNextTime = "";
        if (data.selfreflection) {
          try {
            const parsed = typeof data.selfreflection === 'string'
              ? JSON.parse(data.selfreflection)
              : data.selfreflection;
            if (parsed && typeof parsed === 'object') {
              loadedEffective = parsed.effective || "";
              loadedStuck = parsed.stuck || "";
              loadedNextTime = parsed.next_time || "";
            } else {
              loadedEffective = data.selfreflection;
            }
          } catch (e) {
            loadedEffective = data.selfreflection;
          }
        }
        setReflectionEffective(loadedEffective);
        setReflectionStuck(loadedStuck);
        setReflectionNextTime(loadedNextTime);

        // Baseline for the Save/Complete button's dirty-check - built from
        // these same local values (not read back from React state, which
        // wouldn't be updated yet within this same tick) in the exact
        // shape buildSavePayload(false) produces once state has settled.
        setSavedSnapshot(JSON.stringify({
          ...loadedConsultationData,
          selfreflection: JSON.stringify({ effective: loadedEffective, stuck: loadedStuck, next_time: loadedNextTime }),
          transcript: data.transcript || "",
        }));

        setGeneratingSummary(data.summary_generation_status === 'processing');
        // get_consultation() has no "doctors" key on its response (never
        // did) - doctorDetails was silently always null here, which made
        // canDoctorPrescribeMedicine() default to true regardless of the
        // doctor's real specialization, so Rx/Tests showed even for
        // non-prescribing specializations (psychologist, nutrition, etc.)
        // that correctly hide it on the New page. Fetch the real doctor
        // record the same way ConsultationNew.js does.
        if (data.doctor_id) {
          api.getDoctor(data.doctor_id, { signal: controller.signal })
            .then((docRes) => setDoctorDetails(docRes.data))
            .catch((err) => console.error("Failed to load doctor details:", err));
        } else {
          setDoctorDetails(null);
        }
        setTranscript(data.transcript || "");

        // B. Fetch Patient & History (Dependent on Consultation)
        if (data.patient_id) {
          try {
            const patientRes = await api.getPatient(data.patient_id, { signal: controller.signal });
            if (!controller.signal.aborted) {
              setPatientDetails(patientRes.data);
            }
            try {
              const historyRes = await api.getAllConsultationsOfPatient(data.patient_id);
              if (!controller.signal.aborted) {
                setHistory(historyRes.data?.filter(h => String(h.id) !== String(id)) || []);
              }
            } catch (hErr) {
              console.error("Failed to load patient history", hErr);
            }
          } catch (error) {
            if (error.name !== 'Canceled') console.error("Failed to fetch patient details", error);
          }
        }

        // C. Fetch Actual Cognitive Model
        try {
          const actualModelRes = await api.getActualCognitiveModel(id, { signal: controller.signal });
          if (!controller.signal.aborted) {
            setActualModel((actualModelRes && actualModelRes.data) ? actualModelRes.data : {});
          }
        } catch (err) {
          if (err.name !== 'Canceled') {
            console.error('Error fetching actual model:', err);
            setActualModel({});
          }
        }

      } catch (err) {
        if (err.name !== 'Canceled') {
          console.error("Failed to fetch consultation:", err);
          alert("Error loading consultation");
        }
      } finally {
        if (!controller.signal.aborted) {
          isFetchingCoreRef.current = false;
        }
      }
    })();

    return () => {
      controller.abort();
      isFetchingCoreRef.current = false;
    };
  }, [id]); // Only depends on ID

  // Load prescription header settings
  useEffect(() => {
    const loadHeaderConfig = async () => {
      try {
        const res = await api.getOrgConfig("prescription_header");
        if (res.data && res.data.config_data) {
          setPrescriptionHeaderConfig(res.data.config_data);
          localStorage.setItem("org_prescription_header", JSON.stringify(res.data.config_data));
        }
      } catch (e) {
        setPrescriptionHeaderConfig(getPrescriptionHeaderSettings());
      }
    };
    loadHeaderConfig();
  }, []);

  // 2. Cognitive Reconstruction Fetch (Dependent on ID and Persona Type)
  useEffect(() => {
    if (!id || isCustomPersona) return; // Skip if custom persona or no ID

    const controller = new AbortController();

    (async () => {
      try {
        const reconRes = await api.getCognitiveReconstruction(id, { signal: controller.signal });
        if (controller.signal.aborted) return;
        await fetchReports(id);

        let reconstructionData = reconRes.data;
        // Parse comparison_results if it's a string
        if (reconstructionData?.comparison_results && typeof reconstructionData.comparison_results === 'string') {
          try {
            reconstructionData.comparison_results = JSON.parse(reconstructionData.comparison_results);
          } catch (e) {
            console.error('Error parsing comparison_results:', e);
          }
        }
        setCognitiveReconstruction(reconstructionData);
      } catch (err) {
        if (err.name !== 'Canceled' && err.response?.status !== 404) {
          console.error("Failed to fetch cognitive reconstruction:", err);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [id, isCustomPersona]);

  function handlePatientEdit(data) {
    let patient_data = { ...data, referee_url: `/Consultation-edit/${id}` };
    navigate("/patient-edit/", { state: patient_data });
  }

  const isGP = consultationData?.consultation_type === "general_medicine";
  const isRealPatient = Boolean(patientDetails?.id) && patientDetails?.patienttype !== "Virtual";
  // A non-prescriber's mental-health note prints as a Visit Summary built from
  // the patient summary, this session's homework, agreed goals and next visit.
  const isVisitSummaryMode = !isGP && !canDoctorPrescribeMedicine(doctorDetails);
  const quickInsertLocked = isVisitSummaryMode && ownSummaryText(consultationData?.gp_advice).length < MIN_OWN_SUMMARY_CHARS;
  const showRisk = !isGP && isRealPatient && !isReceptionist && features.has("risk");
  useSessionNoteFormat(appointmentId, Boolean(appointmentId), consultationData, setConsultationData);
  const [askNoteFormat, setAskNoteFormat] = useState(false); // asked at Complete (no clinic default, nothing picked)
  const activeTemplate = noteTemplates.find((t) => t.code === consultationData?.template_code && t.sections) || null;
  // Clinician-written note text, for the risk section's phrase check.
  const riskNoteText = Object.entries(consultationData || {})
    .filter(([k, val]) => typeof val === "string" && !k.startsWith("gp_") && !["status", "sessiondate", "sessiontime", "consultation_type", "what_went_well"].includes(k))
    .map(([, val]) => val).concat(Object.values(consultationData?.template_values || {})).join("\n").slice(0, 20000);
  // Drafts from the call transcript (online mental-health sessions with a real
  // patient), hidden once the note is signed.
  const isOnlineAppt = (meetingInfo?.appttype || "").toLowerCase() === "online";
  // The paid session feedback finished (SessionFeedbackCard): load only its
  // fields, so nothing typed in the note is lost.
  const loadSessionFeedback = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.getConsultation(id);
      setConsultationData((prev) => ({
        ...prev,
        supervisor_evaluation: data.supervisor_evaluation || null,
        what_went_well: data.what_went_well || prev.what_went_well,
        summary_generation_status: data.summary_generation_status || prev.summary_generation_status,
        review_required: data.review_required || false,
      }));
    } catch (e) {
      console.error("Couldn't load the session feedback", e);
    }
  }, [id]);

  const sessionAssist = useSessionAssist(meetingInfo?.id, isOnlineAppt && !isGP && isRealPatient && !isReceptionist);
  const showDrafts = !isGP && !isReceptionist && !noteLocked;
  const [homeworkKey, setHomeworkKey] = useState(0);
  const templateLabels = Object.fromEntries((activeTemplate?.sections || []).map((sec) => [sec.key, sec.label || sec.key]));
  const [planSuggestions, setPlanSuggestions] = useState(null); // AI plan suggestions the clinician ticked
  const draftProps = {
    onApplyPlan: (items) => setPlanSuggestions({ items, at: Date.now() }),
    hook: sessionAssist,
    values: consultationData || {},
    templateLabels,
    templateNames: Object.fromEntries(noteTemplates.map((t) => [t.code, t.name])),
    onApplyNote: (fields) => setConsultationData((prev) => ({ ...prev, ...fields })),
    onApplyTemplate: (fields) => setConsultationData((prev) => ({ ...prev, template_values: { ...(prev.template_values || {}), ...fields } })),
    currentSummary: consultationData?.gp_advice,
    onApplySummary: (text) => setConsultationData((prev) => ({ ...prev, gp_advice: text })),
    patientId: patientDetails?.id,
    session: { consultationId: id, appointmentId },
    onAssigned: () => setHomeworkKey((k) => k + 1),
    sessionDate: consultationData?.sessiondate,
    onAddFollowUps: (items) => setConsultationData((prev) => ({ ...prev, follow_up_items: [...(prev.follow_up_items || []), ...items] })),
  };

  // Tests the AI flagged as required_before_medicine that don't appear to
  // have been added to gp_tests yet - a simple substring check since gp_tests
  // is free text, not a structured/keyed field.
  const missingRequiredTests = aiSuggestedTests.filter(
    (t) => t?.required_before_medicine && t?.test_name &&
      !(consultationData.gp_tests || "").toLowerCase().includes(t.test_name.toLowerCase())
  );

  // Test names parsed out of gp_tests, offered as a "link to test" picker
  // when uploading a report - same parsing rules as TestsTable.js.
  const testOptions = (consultationData.gp_tests || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^\d+[\.\)]\s*|^-\s*/, "").split("|")[0].trim())
    .filter(Boolean);

  // Segmented tab navigation, replacing the old flat accordion stack.
  const gpTabs = [
    { key: "clinical", label: "Clinical Notes", icon: ClipboardList },
    { key: "rx", label: "Prescription & Tests", icon: Pill },
    { key: "advice", label: "Advice", icon: HeartHandshake },
    ...(isRealPatient && !isReceptionist && features.has("toolkit") ? [{ key: "homework", label: "Homework", icon: BookOpenCheck }] : []),
    { key: "followups", label: "Follow-Ups", icon: BellRing },
  ];
  const mhTabs = [
    // In the order a session runs: the note (throughout), the plan agreed
    // with the patient, what they take away, next steps, then the
    // clinician's private reflection afterwards.
    { key: "insights", label: "Clinical Note", icon: BarChart3 },
    ...(isRealPatient && !isReceptionist && features.has("treatment_plan") ? [{ key: "plan", label: "Treatment Plan", icon: Target }] : []),
    { key: "advice", label: "Shared with Patient", icon: HeartHandshake },
    ...(isRealPatient && !isReceptionist && features.has("toolkit") ? [{ key: "homework", label: "Homework", icon: BookOpenCheck }] : []),
    { key: "followups", label: "Follow-Ups", icon: BellRing },
    { key: "reflection", label: "Self-Reflection", icon: PenLine },
  ];
  const tabs = isGP ? gpTabs : mhTabs;
  const [activeTab, setActiveTab] = useState(tabs[0].key);

  // consultation_type (and thus isGP) starts as "mental_health" and only
  // flips to the real value once the async fetch resolves - if activeTab
  // was set against the initial (MH) tab set, it can go stale/invalid the
  // moment isGP flips to true (e.g. "insights" doesn't exist in gpTabs).
  useEffect(() => {
    if (!tabs.some((t) => t.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [isGP]);

  const handlePrintPrescription = async (hideHeader) => {
    // Perform save consultation before printing
    try {
      await handleSave(false);
    } catch (saveErr) {
      console.error("Auto-save before print failed:", saveErr);
    }

    const isHideHeader =
      typeof hideHeader === "boolean"
        ? hideHeader
        : selectedPrintMode === "preprinted";
    const activeConfig = prescriptionHeaderConfig || getPrescriptionHeaderSettings();
    let visitSummary = null;
    if (isVisitSummaryMode && isRealPatient) {
      try {
        visitSummary = (await api.getVisitSummary(patientDetails.id, { consultation_id: id || undefined, appointment_id: appointmentId || undefined })).data;
      } catch (e) {
        console.error("Visit summary fetch failed:", e);
      }
    }
    printMedicalPrescription({
      visitSummary,
      showDiagnosis: summaryIncludeDiagnosis,
      consultationData,
      patient: patientDetails,
      doctor: doctorDetails || JSON.parse(localStorage.getItem("auth") || "{}"),
      headerConfig: activeConfig,
      hideHeader: isHideHeader,
      canPrescribeMedicine: canDoctorPrescribeMedicine(doctorDetails)
    });
    setIsPrintMenuOpen(false);
  };

  const handleSelectPrintMode = (mode) => {
    setSelectedPrintMode(mode);
    try {
      localStorage.setItem("default_prescription_print_mode", mode);
    } catch (e) { }
    handlePrintPrescription(mode === "preprinted");
  };

  const getLanguage = (langStr) => {
    if (!langStr) return 'N/A';
    try {
      const parsed = JSON.parse(langStr);
      return Array.isArray(parsed) ? parsed.join(', ') : langStr;
    } catch {
      return langStr;
    }
  };

  return (
    <DashboardLayout startCollapsed={true}>
      <div className={`min-h-screen bg-secondary-50 -m-4 md:-m-8 p-4 md:p-8 ${videoLayout === "side" ? "sm:pr-[420px] xl:pr-[500px]" : ""}`}>

        {/* Back Button - Styled */}
        <div className="mb-6">
          <Link
            to="/ConsultationHistory"
            className="inline-flex items-center gap-2 text-primary-700 hover:text-primary-900 transition-all font-medium bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm hover:shadow-md border border-primary-200 dark:bg-secondary-800 dark:border-secondary-700 dark:text-primary-300"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>Back to History</span>
          </Link>
        </div>

        <div className="flex flex-col xl:flex-row gap-8">
          {/* =========================================================
              LEFT PANEL (Patient Details, Reports, History)
          ========================================================= */}
          <ConsultationSidePanel
            patient={patientDetails}
            onEditPatient={handlePatientEdit}
            consultationId={id || consultationId}
            appointmentId={appointmentId}
            gpAdvice={consultationData.gp_advice}
            reports={reports}
            reportName={reportName}
            setReportName={setReportName}
            testOptions={testOptions}
            linkedTestName={linkedTestName}
            setLinkedTestName={setLinkedTestName}
            uploadingReports={uploadingReports}
            onFileUpload={handleFileUpload}
            onDownloadReport={handleDownloadReport}
            onDeleteReport={handleDeleteReport}
            currentUserId={currentUserId}
            history={history}
            onSelectHistoryItem={(item) => setSelectedHistoryItem(item)}
          />

          {/* =========================================================
              RIGHT PANEL (Consultation Form & Actions)
          ========================================================= */}
          <div className="flex-1 min-w-0">

            {/* Consultation Form - GLASS CARD */}
            {consultationData && (
              <div className="bg-white/80 backdrop-blur-xl shadow-xl border border-white/50 rounded-2xl overflow-hidden mb-6 transition-all duration-300 hover:shadow-primary-200/20">
                <div className="p-4 sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pb-4 mb-4 border-b border-primary-100 dark:border-secondary-700">
                    <div className="flex items-center gap-3 min-w-0 flex-1 basis-[240px]">
                      <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center text-white shadow-md shrink-0">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-bold text-gray-900 dark:text-primary-100 tracking-tight truncate">
                            {consultationId ? "Session Notes" : "New Session"}
                          </h3>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0 ${isGP ? "bg-teal-100 text-teal-700" : "bg-primary-100 text-primary-700"
                            }`}>
                            {isGP ? "General Medicine" : "Mental Health"}
                          </span>
                        </div>
                        <p className="text-secondary-500 dark:text-secondary-400 font-medium mt-0.5 text-xs truncate">
                          {formatDateTime().displayDate} • {patientDetails?.name || "Patient"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap justify-end ml-auto">
                      {isRealPatient && !isReceptionist && features.has("crisis") && (
                        <CrisisButton compact patientId={patientDetails.id} patientName={patientDetails.name} />
                      )}
                      {isRealPatient && !isReceptionist && features.has("supervision") && (
                        <button type="button" onClick={() => setAskSupervision(true)}
                          className="inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-lg border border-primary-300 bg-white text-primary-700 hover:bg-primary-50 font-bold px-2.5 py-1.5 text-xs">
                          Supervision
                        </button>
                      )}
                      {askSupervision && <AskSupervisionModal patientId={patientDetails.id} patientName={patientDetails.name} consultationId={id} onClose={() => setAskSupervision(false)} />}
                      {canJoinMeeting && !videoCallOpen && (
                        <button
                          type="button"
                          onClick={() => setVideoCallOpen(true)}
                          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold px-2.5 py-1.5 rounded-lg shadow-md transition-all text-xs cursor-pointer"
                        >
                          <Video className="w-3.5 h-3.5" />
                          Join Meeting
                        </button>
                      )}
                      {/* Save - promoted into the header, always reachable regardless of active tab.
                          Once already completed, this becomes a plain update action (no longer
                          re-forces status) and stays disabled until the form actually changes
                          from what's saved - nothing to "complete" or "update" otherwise. */}
                      <button
                        type="button"
                        onClick={() => handleSave(true, consultationData.status !== "completed")}
                        disabled={noteLocked || (consultationData.status === "completed" && !isDirty)}
                        title={noteLocked ? "This note is signed and locked - add an addendum instead" : undefined}
                        className="inline-flex items-center gap-1.5 bg-primary-600 hover:bg-primary-700 text-white font-bold px-2.5 py-1.5 rounded-lg shadow-sm hover:shadow-md transition-all text-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600 disabled:shadow-sm"
                      >
                        {consultationData.status === "completed" && !isDirty && !noteLocked ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
                        {consultationData.status !== "completed" ? "Complete" : noteLocked ? "Save" : isDirty ? "Save" : "Saved"}
                      </button>

                      {/* Medical Prescription Print Split/Dropdown Button */}
                      {(isGP || Boolean(consultationData?.gp_prescription) || Boolean(consultationData?.gp_diagnosis) || Boolean(consultationData?.gp_advice) || (isVisitSummaryMode && isRealPatient)) && (
                        <div className="relative inline-block text-left">
                          <div className="inline-flex rounded-lg shadow-sm relative">
                            <button
                              type="button"
                              onClick={() => handlePrintPrescription(selectedPrintMode === "preprinted")}
                              className="bg-white hover:bg-primary-50 text-primary-600 border border-primary-200 px-2.5 py-1.5 rounded-l-lg flex items-center gap-1.5 shadow-sm hover:shadow-md transition-all font-semibold text-xs cursor-pointer"
                              title={selectedPrintMode === "preprinted" ? "Print on Pre-Printed Pad (Header Hidden)" : "Print Full Medical Prescription (With Header)"}
                            >
                              <Printer className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setIsPrintMenuOpen(!isPrintMenuOpen)}
                              className="bg-white hover:bg-primary-50 text-primary-600 border border-l-0 border-primary-200 px-1.5 py-1.5 rounded-r-lg flex items-center justify-center transition-all cursor-pointer"
                              title="Prescription Print Options (Saves default)"
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>

                          {isPrintMenuOpen && (
                            <>
                              <div
                                className="fixed inset-0 z-40"
                                onClick={() => setIsPrintMenuOpen(false)}
                              />
                              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-secondary-800 rounded-xl shadow-xl border border-gray-100 dark:border-secondary-700 z-50 py-1.5 animate-in fade-in zoom-in-95 duration-100">
                                <div className="px-3 py-1.5 text-[11px] font-bold text-gray-400 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-secondary-700 mb-1 flex items-center justify-between">
                                  <span>{isVisitSummaryMode ? "Visit Summary Print Options" : "Prescription Print Options"}</span>
                                  <span className="text-[9.5px] font-normal text-primary-600 dark:text-primary-400 lowercase">(saved as default)</span>
                                </div>
                                {isVisitSummaryMode && Boolean(consultationData?.gp_diagnosis) && (
                                  <label className="flex items-start gap-2 px-3 py-2 text-xs text-gray-700 dark:text-gray-200 border-b border-gray-100 dark:border-secondary-700 cursor-pointer normal-case font-sans tracking-normal">
                                    <input type="checkbox" className="mt-0.5 h-3.5 w-3.5" checked={summaryIncludeDiagnosis} onChange={(e) => setSummaryIncludeDiagnosis(e.target.checked)} />
                                    <span>Include diagnosis<span className="block text-[10.5px] text-gray-500">Off by default: patients often show this to employers or colleges.</span></span>
                                  </label>
                                )}

                                <button
                                  type="button"
                                  onClick={() => handleSelectPrintMode("full")}
                                  className={`w-full text-left px-3 py-2 text-xs hover:bg-primary-50 dark:hover:bg-secondary-700 flex items-start justify-between gap-2.5 transition-colors cursor-pointer ${selectedPrintMode === "full" ? "bg-primary-50/70 text-primary-700 font-bold" : "text-gray-700 dark:text-gray-200"
                                    }`}
                                >
                                  <div className="flex items-start gap-2.5">
                                    <Printer className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <div className="font-bold">Print Full Letterhead</div>
                                      <div className="text-[10.5px] text-gray-500 dark:text-gray-400 font-normal">
                                        Includes Hospital Name, Contact, & Logo
                                      </div>
                                    </div>
                                  </div>
                                  {selectedPrintMode === "full" && (
                                    <span className="text-[10px] bg-primary-100 text-primary-700 font-bold px-1.5 py-0.5 rounded-md self-center flex-shrink-0">
                                      Default
                                    </span>
                                  )}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleSelectPrintMode("preprinted")}
                                  className={`w-full text-left px-3 py-2 text-xs hover:bg-primary-50 dark:hover:bg-secondary-700 flex items-start justify-between gap-2.5 transition-colors cursor-pointer border-t border-gray-50 dark:border-secondary-700 ${selectedPrintMode === "preprinted" ? "bg-primary-50/70 text-primary-700 font-bold" : "text-gray-700 dark:text-gray-200"
                                    }`}
                                >
                                  <div className="flex items-start gap-2.5">
                                    <FileText className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                      <div className="font-bold">Print on Pre-Printed Pad</div>
                                      <div className="text-[10.5px] text-gray-500 dark:text-gray-400 font-normal">
                                        Hides header for pre-printed stationery
                                      </div>
                                    </div>
                                  </div>
                                  {selectedPrintMode === "preprinted" && (
                                    <span className="text-[10px] bg-primary-100 text-primary-700 font-bold px-1.5 py-0.5 rounded-md self-center flex-shrink-0">
                                      Default
                                    </span>
                                  )}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Download PDF Dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setIsDownloadMenuOpen(!isDownloadMenuOpen)}
                          disabled={conversationLoading || !consultationData?.supervisor_evaluation}
                          className="bg-white hover:bg-primary-50 text-primary-600 border border-primary-200 dark:bg-secondary-800 dark:border-secondary-700 dark:text-primary-300 dark:hover:bg-secondary-700 px-2.5 py-1.5 rounded-lg flex items-center gap-1 shadow-sm hover:shadow-md transition-all font-semibold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!consultationData?.supervisor_evaluation ? "Generate report first" : "Download PDF Options"}
                        >
                          <Download className="w-3.5 h-3.5" />
                          <ChevronDown className="w-3 h-3" />
                        </button>

                        {isDownloadMenuOpen && (
                          <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-secondary-800 border border-primary-200 dark:border-secondary-700 shadow-xl rounded-xl z-50 overflow-hidden">
                            <div className="p-1 flex flex-col gap-1">
                              <button
                                onClick={() => { setIsDownloadMenuOpen(false); handleDownloadConversationPdf('summary_only'); }}
                                className="text-left px-3 py-2 hover:bg-primary-50 dark:hover:bg-secondary-700 rounded-lg text-sm text-secondary-800 dark:text-secondary-200 font-medium transition-colors"
                              >
                                Summary Only
                              </button>
                              <button
                                onClick={() => { setIsDownloadMenuOpen(false); handleDownloadConversationPdf('report_no_transcript'); }}
                                className="text-left px-3 py-2 hover:bg-primary-50 dark:hover:bg-secondary-700 rounded-lg text-sm text-secondary-800 dark:text-secondary-200 font-medium transition-colors"
                              >
                                Report w/o Transcript
                              </button>
                              <button
                                onClick={() => { setIsDownloadMenuOpen(false); handleDownloadConversationPdf('full'); }}
                                className="text-left px-3 py-2 hover:bg-primary-50 dark:hover:bg-secondary-700 rounded-lg text-sm text-secondary-800 dark:text-secondary-200 font-medium transition-colors border-b border-primary-100 dark:border-secondary-700 pb-2"
                              >
                                Full Report
                              </button>
                              <button
                                onClick={() => { setIsDownloadMenuOpen(false); handleDownloadConversationPdf('guide_only'); }}
                                className="text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg text-sm text-blue-600 dark:text-blue-400 font-bold transition-colors mt-1"
                              >
                                How to read the report
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Expert Review Button - Admin Only */}
                      {consultationData.supervisor_evaluation && isAdmin && (
                        <button
                          onClick={() => navigate(`/consultation/${id}/report-editor`)}
                          className="bg-primary-500 hover:bg-primary-600 text-white border border-primary-600 dark:bg-primary-600 dark:border-primary-700 dark:hover:bg-primary-700 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm hover:shadow-md transition-all font-semibold text-xs"
                          title="Expert Report Editor"
                        >
                          <FileEdit className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {/* Call sessions: the transcript and the paid feedback, as header buttons */}
                      {(meetingInfo?.appttype || "").toLowerCase() === "online" && meetingInfo?.id && (
                        <SessionTranscript appointmentId={meetingInfo.id} onAssistChange={sessionAssist.setAssist} onAvailable={setHasCallTranscript} />
                      )}
                      {(meetingInfo?.appttype || "").toLowerCase() === "online" && meetingInfo?.id && !isGP && isRealPatient && !isReceptionist && (
                        <SessionFeedbackCard appointmentId={meetingInfo.id} rolesKey={JSON.stringify(sessionAssist.assist?.speaker_roles || null)}
                          onReady={loadSessionFeedback} />
                      )}
                      {/* View Conversation Button (uploads and practice sessions; a call session uses Transcript) */}
                      {!hasCallTranscript && <button
                        onClick={handleOpenConversationModal}
                        className="bg-white hover:bg-primary-50 text-primary-600 border border-primary-200 dark:bg-secondary-800 dark:border-secondary-700 dark:text-primary-300 dark:hover:bg-secondary-700 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 shadow-sm hover:shadow-md transition-all font-semibold text-xs"
                        title="View Conversation"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                      </button>}
                    </div>
                  </div>

                  {(meetingInfo?.appttype || "").toLowerCase() === "online" && meetingInfo?.meeting_room_name && meetingInfo?.id && (
                    <TranscriptionConsentNotice appointmentId={meetingInfo.id} patientName={patientDetails?.name} />
                  )}


                  {features.has("note_signing") && consultationId && isRealPatient && (
                    <div className="mb-4">
                      <NoteSigningPanel consultationId={consultationId} onLockedChange={setNoteLocked} refreshKey={consultationData.status} summaryFromTab={!isGP} />
                    </div>
                  )}

                  {/* Folder-tab navigation, on a visible tinted backdrop band so it
                      doesn't wash out against the white card - active tab is a raised
                      indigo-bordered chip, inactive tabs sit flatter on solid gray. */}
                  <div className="flex items-end gap-1 px-1.5 pt-1.5 pb-0.5 mb-4 overflow-x-auto bg-secondary-50 dark:bg-secondary-800/40 rounded-xl">
                    <nav className="flex items-end gap-1 min-w-full sm:min-w-0" aria-label="Tabs">
                      {tabs.map((tab) => {
                        const TabIcon = tab.icon;
                        const isActive = activeTab === tab.key;
                        return (
                          <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            aria-current={isActive ? "page" : undefined}
                            className={`relative flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-t-lg text-xs md:text-sm font-bold whitespace-nowrap border-2 border-b-0 transition-colors cursor-pointer ${isActive
                              ? "bg-white dark:bg-secondary-900 text-primary-700 dark:text-primary-300 border-primary-500 shadow-md"
                              : "bg-secondary-200 dark:bg-secondary-700/60 text-secondary-600 dark:text-secondary-300 border-transparent hover:bg-secondary-300/70 hover:text-secondary-800 dark:hover:text-secondary-100"
                              }`}
                          >
                            <TabIcon className={`w-4 h-4 shrink-0 ${isActive ? "text-primary-600" : "text-secondary-500"}`} />
                            {tab.label}
                          </button>
                        );
                      })}
                    </nav>
                  </div>

                  {/* Editable until signed; a signed note's own tabs are read-only (changes go in as addenda).
                      Treatment plan, follow-ups and homework save separately and stay usable. */}
                  {noteLocked && ["clinical", "rx", "insights", "reflection"].includes(activeTab) && (
                    <p className="mb-3 rounded-lg bg-secondary-100 px-3 py-2 text-xs text-secondary-700">This note is signed, so it's read-only. Add an addendum to record changes.</p>
                  )}
                  <div className={`space-y-6 ${noteLocked && ["clinical", "rx", "insights", "reflection"].includes(activeTab) ? "pointer-events-none select-text opacity-80" : ""}`}
                    aria-disabled={noteLocked && ["clinical", "rx", "insights", "reflection"].includes(activeTab) ? "true" : undefined}>
                      {/* ============ GP TAB: Clinical Notes ============ */}
                      {isGP && activeTab === "clinical" && isRealPatient && !isReceptionist && <PreviousSessionCarryover patientId={patientDetails.id} consultationId={id} appointmentId={appointmentId} mode="plan" />}
                      {isGP && activeTab === "clinical" && (
                        <div className="p-4 bg-teal-50/20 border border-teal-200/60 rounded-xl space-y-4">
                            {/* 2 Equal Symmetrical Columns */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                              <div>
                                <div className="flex items-center justify-between mb-1.5 h-[26px]">
                                  <Label className="text-gray-700 font-semibold text-xs">Main Complaints & History of Illness</Label>
                                </div>
                                <ClinicalTypeaheadTextarea
                                  name="gp_symptoms"
                                  value={consultationData.gp_symptoms}
                                  onChange={handleChange}
                                  category="symptoms"
                                  placeholder="Patient's primary symptoms, onset, duration, and complaints..."
                                  minHeight="160px"
                                  className="text-sm bg-white"
                                />
                              </div>
                              <div>
                                <div className="flex items-center justify-between mb-1.5 h-[26px]">
                                  <Label className="text-gray-700 font-semibold text-xs">Vitals & Physical Examination</Label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentText = (consultationData.gp_vitals || "").trim();
                                      const standardPanels = [
                                        { regex: /(?:^|\s|\n)(?:BP|B\.P\.)\s*:/i, line: "BP: " },
                                        { regex: /(?:^|\s|\n)(?:Pulse|HR|Heart Rate)\s*:/i, line: "Pulse: " },
                                        { regex: /(?:^|\s|\n)(?:Temp|Temperature)\s*:/i, line: "Temp: " },
                                        { regex: /(?:^|\s|\n)(?:SpO2|Oxygen)\s*:/i, line: "SpO2: " },
                                        { regex: /(?:^|\s|\n)(?:Weight|Wt)\s*:/i, line: "Weight: " },
                                        { regex: /(?:^|\s|\n)(?:Height|Ht)\s*:/i, line: "Height: " },
                                        { regex: /(?:^|\s|\n)(?:General Exam|Physical Exam|Exam)\s*:/i, line: "General Exam: " }
                                      ];

                                      const missingLines = standardPanels
                                        .filter((item) => !item.regex.test(currentText))
                                        .map((item) => item.line);

                                      if (missingLines.length === 0) return;

                                      const textToAppend = missingLines.join("\n");
                                      const updatedVitals = currentText ? `${currentText}\n${textToAppend}` : textToAppend;
                                      setConsultationData((prev) => ({
                                        ...prev,
                                        gp_vitals: updatedVitals
                                      }));
                                    }}
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900 bg-teal-50 hover:bg-teal-100 border border-teal-200 px-2 py-0.5 rounded-md transition-colors shadow-2xs cursor-pointer"
                                    title="Insert standard vitals panel (avoids duplicates)"
                                  >
                                    <Sparkles className="w-3 h-3 text-teal-600" />
                                    + Standard Panel
                                  </button>
                                </div>
                                <ClinicalTypeaheadTextarea
                                  name="gp_vitals"
                                  value={consultationData.gp_vitals}
                                  onChange={handleChange}
                                  category="vitals"
                                  placeholder="BP, Temp, HR, exam findings..."
                                  minHeight="160px"
                                  className="text-sm bg-white"
                                />
                              </div>
                            </div>

                            {/* Diagnosis - clinical content, view-only for a receptionist */}
                            <div className="bg-white p-4 rounded-xl border border-gray-200">
                              <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">Clinical Diagnosis / Assessment</Label>
                              <ClinicalTypeaheadTextarea
                                name="gp_diagnosis"
                                value={consultationData.gp_diagnosis}
                                onChange={handleChange}
                                category="diagnosis"
                                placeholder="Doctor Assessment, Clinical diagnosis, differential diagnosis, ICD-10 notes..."
                                minHeight="100px"
                                readOnly={isReceptionist}
                                disabled={isReceptionist}
                                className={`mt-1 text-sm ${isReceptionist ? "bg-gray-50 text-gray-500 cursor-not-allowed" : "bg-white"}`}
                              />
                            </div>
                        </div>
                      )}

                      {/* ============ GP TAB: Prescription & Tests ============ */}
                      {isGP && activeTab === "rx" && (
                        <div className="space-y-3">
                          {canDoctorPrescribeMedicine(doctorDetails) ? (
                              <>
                                {/* AI Rx generation is a clinical action - not for a receptionist */}
                                {!isReceptionist && (
                                <div className="flex items-start justify-between gap-3 bg-indigo-50/60 border border-indigo-100 rounded-xl px-3 py-2">
                                  <div className="flex items-start gap-1.5">
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                                    <p className="text-[11px] text-indigo-700 leading-tight">
                                      Generate a suggested prescription & advice from the symptoms, vitals, and diagnosis entered above.{" "}
                                      <br />
                                      <span className="font-semibold text-amber-700">
                                        AI-generated — always review and verify clinically before finalizing. Do not follow suggestions blindly.
                                      </span>
                                    </p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={handleGenerateAIPrescription}
                                    disabled={generatingRx}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors shadow-sm shrink-0 cursor-pointer"
                                  >
                                    {generatingRx ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <Wand2 className="w-3.5 h-3.5" />
                                    )}
                                    {generatingRx ? "Generating..." : "Generate with AI"}
                                  </button>
                                </div>
                                )}

                                {missingRequiredTests.length > 0 && (
                                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                                    <p className="text-[11px] text-amber-800 leading-tight">
                                      <span className="font-bold">Test required before starting this medicine: </span>
                                      {missingRequiredTests.map((t) => t.test_name).join(", ")} — add {missingRequiredTests.length > 1 ? "these tests" : "this test"} to Tests / Investigations below, or confirm it's already been done, before finalizing the prescription.
                                    </p>
                                  </div>
                                )}

                                {/* 3-Column Prescription Table - clinical content, view-only for a receptionist */}
                                <PrescriptionTable
                                  value={consultationData.gp_prescription}
                                  onChange={handleChange}
                                  canPrescribeMedicine={canDoctorPrescribeMedicine(doctorDetails)}
                                  disabled={isReceptionist}
                                />

                                <TestsTable
                                  value={consultationData.gp_tests}
                                  onChange={handleChange}
                                  disabled={isReceptionist}
                                />
                              </>
                          ) : (
                            <div className="text-center py-10 px-4 text-sm text-gray-400 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                              Prescription and tests are not applicable for this doctor's specialization.
                            </div>
                          )}
                        </div>
                      )}

                      {/* ============ GP TAB: Advice ============ */}
                      {isGP && activeTab === "advice" && (
                            <div className="bg-gray-50/70 p-3 rounded-xl border border-gray-200">
                              <Label className="text-gray-700 font-semibold text-xs mb-1.5 block">General Advice / Instructions & Follow-up</Label>
                              <ClinicalTypeaheadTextarea
                                name="gp_advice"
                                value={consultationData.gp_advice}
                                onChange={handleChange}
                                category="advice"
                                placeholder="Dietary advice, lifestyle changes, follow up dates..."
                                minHeight="300px"
                                readOnly={isReceptionist}
                                disabled={isReceptionist}
                                className={`mt-1 text-xs ${isReceptionist ? "bg-gray-50 text-gray-500 cursor-not-allowed" : "bg-white"}`}
                              />
                            </div>
                      )}

                      {/* ============ Follow-Ups (shared by GP and MH) ============ */}
                      {!isGP && activeTab === "plan" && isRealPatient && (
                        <div className="space-y-2">
                          <p className="text-xs text-secondary-500">Agree the goals with {patientDetails.name} during the session. It saves on its own (Save plan). Only what is recorded in this session shows here; every version is on the patient file.</p>
                          {showDrafts && <SessionDraftBox {...draftProps} section="plan" />}
                          <TreatmentPlanPanel patientId={patientDetails.id} patientName={patientDetails.name} session={{ consultationId: id, appointmentId }} incoming={planSuggestions} />
                        </div>
                      )}

                      {activeTab === "followups" && isRealPatient && !isReceptionist && <PreviousSessionCarryover patientId={patientDetails.id} consultationId={id} appointmentId={appointmentId} mode="followups" />}
                      {showDrafts && activeTab === "followups" && <SessionDraftBox {...draftProps} section="follow_ups" />}
                      {activeTab === "followups" && (
                        <FollowUpTable
                          value={consultationData.follow_up_items}
                          onChange={handleChange}
                          sessionDate={consultationData.sessiondate}
                          hasAppointment={!!appointmentId}
                          disabled={isReceptionist}
                          suggestedPatientMessage={isGP ? consultationData.gp_advice : (patientMessageDraft || consultationData.gp_advice)}
                        />
                      )}

                      {/* ============ MH TAB: Session Insights ============ */}
                      {!isGP && activeTab === "insights" && isRealPatient && !isReceptionist && <PreviousSessionCarryover patientId={patientDetails.id} consultationId={id} appointmentId={appointmentId} mode="plan" />}
                      {showDrafts && activeTab === "insights" && <SessionDraftBox {...draftProps} section="note" />}
                      {showDrafts && activeTab === "insights" && <SessionDraftBox {...draftProps} section="risk" />}
                      {!isGP && activeTab === "insights" && !isReceptionist && (
                        <div className="mb-5">
                          <NoteTemplatePicker templates={noteTemplates} code={consultationData.template_code}
                            hasContent={Object.values(consultationData.template_values || {}).some((v) => (v || "").trim())}
                            onChange={(code) => { rememberSessionNoteFormat(appointmentId, code); setConsultationData((prev) => ({ ...prev, template_code: code, template_values: code === "default" ? null : (prev.template_values || {}) })); }} />
                        </div>
                      )}
                      {/* The patient's case formulation, read-only, to inform the session. */}
                      {!isGP && activeTab === "insights" && isRealPatient && !isReceptionist && features.has("formulation") && (
                        <details className="mb-4 rounded-xl border border-violet-200 bg-violet-50/40 p-3">
                          <summary className="cursor-pointer text-sm font-semibold text-violet-900">Case formulation</summary>
                          <div className="mt-3 space-y-2">
                            <FormulationPanel patientId={patientDetails.id} compact />
                            <a href={`/patient/${patientDetails.id}?tab=formulation`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-violet-800 underline">Open or update on the patient file</a>
                          </div>
                        </details>
                      )}
                      {!isGP && activeTab === "insights" && (activeTemplate ? (
                        <NoteTemplateSections template={activeTemplate} values={consultationData.template_values} disabled={isReceptionist}
                          onChange={(v) => setConsultationData((prev) => ({ ...prev, template_values: v }))} />
                      ) : (
                        <SessionInsightsPanel
                          consultationData={consultationData}
                          generatingSummary={generatingSummary}
                          onGenerateSummary={handleGenerateSummary}
                          isAdmin={isAdmin}
                          isReceptionist={isReceptionist}
                          onChange={handleChange}
                        />
                      ))}
                      {!isGP && activeTab === "insights" && showRisk && (
                        <div className="mt-5">
                          <RiskAssessmentSection value={riskData} onChange={setRiskData} noteText={riskNoteText} />
                        </div>
                      )}

                      {/* ============ MH TAB: Patient Advice & Care Plan ============ */}
                      {showDrafts && activeTab === "advice" && <SessionDraftBox {...draftProps} section="patient_summary" />}
                      {!isGP && activeTab === "advice" && (
                        <div className="p-4 space-y-4 bg-emerald-50/20 border border-emerald-200/60 rounded-xl">
                              <div>
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                                  <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Label className="text-emerald-900 font-bold text-xs">{isVisitSummaryMode ? "① Summary for the patient" : "① Advice & instructions"}</Label>
                                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10.5px] font-semibold px-2 py-0.5">🖨 Prints on the {canDoctorPrescribeMedicine(doctorDetails) ? "prescription" : "visit summary"}</span>
                                    </div>
                                    <p className="text-[11px] text-emerald-700/80">{isVisitSummaryMode ? "A few plain lines: what you talked about and what to try. Homework, goals and the next visit are added from their own tabs." : "In your own words. This exact text prints under \"General advice & instructions\": habits, coping exercises, things to avoid, homework."}</p>
                                    {quickInsertLocked && <p className="text-[11px] text-amber-700 mt-0.5">Write a line or two in your own words first (what you talked about, what you agreed). The quick inserts unlock after that.</p>}
                                  </div>
                                  {/* Quick Template Insertion Buttons - modify gp_advice, not receptionist-editable */}
                                  {!isReceptionist && (
                                  <div className="flex flex-wrap gap-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const snippet = "• Sleep Hygiene: Go to bed and wake up at consistent times daily. Avoid screen use 1 hour before sleep.";
                                        setConsultationData(prev => ({
                                          ...prev,
                                          gp_advice: prev.gp_advice ? `${prev.gp_advice}\n${snippet}` : snippet
                                        }));
                                      }}
                                      disabled={quickInsertLocked}
                                    title={quickInsertLocked ? "Write a line or two about this session first" : undefined}
                                    className="text-[10.5px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-2 py-0.5 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-100"
                                    >
                                      + Sleep Hygiene
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const snippet = "• Anxiety Management: Practice 4-7-8 diaphragmatic breathing for 5 minutes twice daily.";
                                        setConsultationData(prev => ({
                                          ...prev,
                                          gp_advice: prev.gp_advice ? `${prev.gp_advice}\n${snippet}` : snippet
                                        }));
                                      }}
                                      disabled={quickInsertLocked}
                                    title={quickInsertLocked ? "Write a line or two about this session first" : undefined}
                                    className="text-[10.5px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-2 py-0.5 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-100"
                                    >
                                      + Stress Relief
                                    </button>
                                    {!features.has("toolkit") && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const snippet = "• Homework: Complete daily thought record journal when feeling anxious or overwhelmed.";
                                        setConsultationData(prev => ({
                                          ...prev,
                                          gp_advice: prev.gp_advice ? `${prev.gp_advice}\n${snippet}` : snippet
                                        }));
                                      }}
                                      disabled={quickInsertLocked}
                                    title={quickInsertLocked ? "Write a line or two about this session first" : undefined}
                                    className="text-[10.5px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-2 py-0.5 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-100"
                                    >
                                      + CBT Homework
                                    </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const snippet = "• Things to Avoid: Avoid late-night caffeine, alcohol, excessive news/social media, and self-isolation.";
                                        setConsultationData(prev => ({
                                          ...prev,
                                          gp_advice: prev.gp_advice ? `${prev.gp_advice}\n${snippet}` : snippet
                                        }));
                                      }}
                                      disabled={quickInsertLocked}
                                    title={quickInsertLocked ? "Write a line or two about this session first" : undefined}
                                    className="text-[10.5px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-2 py-0.5 rounded transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-emerald-100"
                                    >
                                      + Things to Avoid
                                    </button>
                                  </div>
                                  )}
                                </div>
                                <ClinicalTypeaheadTextarea
                                  name="gp_advice"
                                  value={consultationData.gp_advice}
                                  onChange={handleChange}
                                  category="advice"
                                  placeholder="Enter details doctor wants to share with patient (medication instructions, diet, exercise, coping skills, habits to build, things to avoid, homework)..."
                                  minHeight="400px"
                                  readOnly={isReceptionist}
                                  disabled={isReceptionist}
                                  className={`text-sm ${isReceptionist ? "bg-gray-50 text-gray-500 cursor-not-allowed" : "bg-white"}`}
                                />
                              </div>

                              {/* Generate & Send to Patient - rewrites the advice above into a
                                  warm, second-person draft and sends it straight through the
                                  messaging feature, right where the doctor is already writing
                                  the advice - not a receptionist action. */}
                              {!isReceptionist && (
                                <div className="mt-1 rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Label className="text-sky-900 font-bold text-xs">② Chat message to the patient (optional)</Label>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 text-[10.5px] font-semibold px-2 py-0.5">💬 Sent to the patient's app · not printed</span>
                                  </div>
                                  <p className="text-[11px] text-sky-800/80 mt-0.5">AI turns the advice above into a friendly message. Edit it, then send. It doesn't change the advice or the printout.</p>
                                </div>
                                    <button
                                      type="button"
                                      onClick={handleGeneratePatientMessage}
                                      disabled={!consultationData.gp_advice?.trim() || generatingPatientMessage}
                                      title={consultationData.gp_advice?.trim() ? "" : "Fill in the advice above first"}
                                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 bg-emerald-100 hover:bg-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1 rounded-md transition-colors cursor-pointer"
                                    >
                                      {generatingPatientMessage ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      ) : (
                                        <Sparkles className="w-3.5 h-3.5" />
                                      )}
                                      {generatingPatientMessage ? "Drafting…" : "Draft message with AI"}
                                    </button>
                                  </div>
                                  {patientMessageDraft && (
                                    <div className="bg-white rounded-lg border border-emerald-200 p-3 space-y-2">
                                      <Textarea
                                        value={patientMessageDraft}
                                        onChange={(e) => setPatientMessageDraft(e.target.value)}
                                        className="text-sm min-h-[120px]"
                                      />
                                      <div className="flex justify-end">
                                        <button
                                          type="button"
                                          onClick={handleSendPatientMessage}
                                          disabled={!patientMessageDraft.trim() || sendingPatientMessage || !appointmentId}
                                          title={appointmentId ? "" : "No appointment linked yet"}
                                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors"
                                        >
                                          {sendingPatientMessage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                          {sendingPatientMessage ? "Sending…" : "Send to Patient"}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  {patientMessageToast && (
                                    <p className={`text-[11px] font-semibold ${patientMessageToast.type === "success" ? "text-emerald-700" : "text-red-600"}`}>
                                      {patientMessageToast.text}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* This tab's text is part of the consultation itself; save it here
                                  without completing (the other tabs save their own records). */}
                              {!isReceptionist && (
                                <div className="flex items-center justify-end gap-2">
                                  <span className="text-[11px] text-secondary-500">{noteLocked ? "This note is signed. Add an addendum instead." : isDirty ? "Unsaved changes" : "All changes saved"}</span>
                                  <button type="button" onClick={() => handleSave(true, false)} disabled={noteLocked || !isDirty}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 px-3 py-1.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600">
                                    <Save className="w-3.5 h-3.5" /> Save
                                  </button>
                                </div>
                              )}

                              {isVisitSummaryMode && isRealPatient && (
                                <PatientTakeawayPreview
                                  patientId={patientDetails.id}
                                  session={{ consultationId: id, appointmentId: appointmentId }}
                                  summaryText={consultationData.gp_advice}
                                  tabKeys={tabs.map((t) => t.key)}
                                  onGoToTab={setActiveTab}
                                  onPrint={() => handlePrintPrescription(selectedPrintMode === "preprinted")}
                                  refreshKey={activeTab}
                                />
                              )}

                              {/* Optional Prescription Table - clinical content, view-only for a receptionist */}
                              {canDoctorPrescribeMedicine(doctorDetails) && (
                                <div className="border-t border-emerald-100 dark:border-emerald-900/30 pt-3 space-y-3">
                                  <Label className="text-emerald-900 dark:text-emerald-200 font-bold text-xs mb-1 block">℞ Medications & Supplements (Optional)</Label>
                                  {missingRequiredTests.length > 0 && (
                                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2">
                                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                                      <p className="text-[11px] text-amber-800 leading-tight">
                                        <span className="font-bold">Test required before starting this medicine: </span>
                                        {missingRequiredTests.map((t) => t.test_name).join(", ")} — add {missingRequiredTests.length > 1 ? "these tests" : "this test"} to Tests / Investigations below, or confirm it's already been done, before finalizing the prescription.
                                      </p>
                                    </div>
                                  )}
                                  <PrescriptionTable
                                    value={consultationData.gp_prescription}
                                    onChange={handleChange}
                                    canPrescribeMedicine={canDoctorPrescribeMedicine(doctorDetails)}
                                    disabled={isReceptionist}
                                  />
                                  <TestsTable
                                    value={consultationData.gp_tests}
                                    onChange={handleChange}
                                    disabled={isReceptionist}
                                  />
                                </div>
                              )}
                        </div>
                      )}

                      {/* ============ MH TAB: Self-Reflection ============ */}
                      {/* Homework tab: what was given in this session (toolkit); suggestions come from the note text. */}
                      {activeTab === "homework" && isRealPatient && !isReceptionist && features.has("toolkit") && (
                        <div className="rounded-xl border border-secondary-200 bg-white p-4">
                          {!isGP && <SessionDraftBox {...draftProps} section="homework" />}
                          <HomeworkPanel key={homeworkKey} patientId={patientDetails.id} patientName={patientDetails.name} session={{ consultationId: id, appointmentId }}
                            noteText={[...Object.values(consultationData || {}), ...Object.values(consultationData?.template_values || {})].filter((v) => typeof v === "string").join(" ")} />
                        </div>
                      )}

                      {!isGP && activeTab === "reflection" && (
                        <div className="!border-primary-400 !border-2 rounded-xl bg-primary-50/20 dark:bg-primary-900/20 shadow-md overflow-hidden">
                            <div className="text-center font-bold mt-2 text-primary-700 dark:text-primary-300 uppercase text-xs tracking-wider">Therapist Self Reflection</div>
                            <div className="p-6 space-y-6">
                              <div className="space-y-2">
                                <Label className="text-primary-800 dark:text-primary-300 font-bold flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                                  What felt most effective?
                                </Label>
                                <Textarea
                                  value={reflectionEffective}
                                  onChange={(e) => isDoctor && setReflectionEffective(e.target.value)}
                                  placeholder="e.g. Using the downward arrow technique helped elicit the core belief..."
                                  readOnly={!isDoctor}
                                  disabled={!isDoctor}
                                  className={`border-primary-200 dark:border-primary-700 focus:ring-primary-400 rounded-xl min-h-[100px] ${!isDoctor ? "bg-secondary-50 dark:bg-secondary-900 text-secondary-500 italic cursor-not-allowed" : "bg-white dark:bg-secondary-800"}`}
                                />
                              </div>

                              <div className="space-y-2">
                                <Label className="text-primary-800 dark:text-primary-300 font-bold flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                                  Where did you feel stuck OR deviated from the plan?
                                </Label>
                                <Textarea
                                  value={reflectionStuck}
                                  onChange={(e) => isDoctor && setReflectionStuck(e.target.value)}
                                  placeholder="e.g. Addressing the patient's hopelessness felt overwhelming..."
                                  readOnly={!isDoctor}
                                  disabled={!isDoctor}
                                  className={`border-primary-200 dark:border-primary-700 focus:ring-primary-400 rounded-xl min-h-[100px] ${!isDoctor ? "bg-secondary-50 dark:bg-secondary-900 text-secondary-500 italic cursor-not-allowed" : "bg-white dark:bg-secondary-800"}`}
                                />
                              </div>

                              <div className="space-y-2">
                                <Label className="text-primary-800 dark:text-primary-300 font-bold flex items-center gap-2">
                                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                                  What would you try differently next session?
                                </Label>
                                <Textarea
                                  value={reflectionNextTime}
                                  onChange={(e) => isDoctor && setReflectionNextTime(e.target.value)}
                                  placeholder="e.g. Spend more time on stabilization before moving to cognitive work..."
                                  readOnly={!isDoctor}
                                  disabled={!isDoctor}
                                  className={`border-primary-200 dark:border-primary-700 focus:ring-primary-400 rounded-xl min-h-[100px] ${!isDoctor ? "bg-secondary-50 dark:bg-secondary-900 text-secondary-500 italic cursor-not-allowed" : "bg-white dark:bg-secondary-800"}`}
                                />
                              </div>
                            </div>
                        </div>
                      )}

                      {/* Cognitive Model Reconstruction feature - commented out, kept for reference.
                          reconstructionFormData/cognitiveReconstruction/actualModel state above is
                          still live (read inside handleSave) even with this UI disabled. */}
                      {/* {!isCustomPersona && (
                    <AccordionItem title="🧠 Cognitive Model Reconstruction" className="!border-primary-400 !border-2 rounded-xl bg-primary-50/20 dark:bg-primary-900/20 shadow-md overflow-hidden">
                      <div className="p-6">
                        {loadingReconstruction ? (
                          <div className="text-center py-12 text-primary-600 dark:text-primary-400 font-medium animate-pulse">
                            Loading Analysis...
                          </div>
                        ) : (
                          <div>
                            <CognitiveModelReconstruction
                              consultationId={id || consultationId}
                              existingReconstruction={cognitiveReconstruction}
                              onDataChange={setReconstructionFormData}
                            />
                            {cognitiveReconstruction?.comparison_results && (
                              <div className="mt-8 pt-8 border-t border-primary-200 dark:border-secondary-700">
                                <CognitiveModelComparison
                                  comparisonData={cognitiveReconstruction.comparison_results}
                                  doctorReconstruction={cognitiveReconstruction}
                                  actualModel={actualModel}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </AccordionItem>
                  )} */}
                    </div>
                  </div>
                </div>
            )}

          </div>
        </div>

        {/* History Modal Portal */}
        {selectedHistoryItem && (
          <HistoryModal
            appt={selectedHistoryItem}
            onClose={() => setSelectedHistoryItem(null)}
          />
        )}

        <NoteFormatPrompt open={askNoteFormat} templates={noteTemplates} current={consultationData.template_code || "default"} onLater={() => setAskNoteFormat(false)}
          onPick={async (code) => {
            setAskNoteFormat(false);
            await api.setNoteFormat(appointmentId, code).catch(() => {});
            if (code === (consultationData.template_code || "default")) { handleSave(true, true); return; }
            setConsultationData((prev) => ({ ...prev, template_code: code, template_values: code === "default" ? null : (prev.template_values || {}) }));
          }} />
        {videoCallOpen && meetingInfo && (
          <VideoCallModal appointment={meetingInfo} defaultLayout="side" onLayoutChange={setVideoLayout} onClose={() => setVideoCallOpen(false)} />
        )}

        {isConversationModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl max-h-[85vh] w-full max-w-4xl overflow-hidden flex flex-col border border-white/20">
              <div className="flex items-center justify-between border-b px-6 py-4 bg-slate-50">
                <h4 className="text-xl font-bold text-slate-800">Transcript History</h4>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCopyTranscript}
                    disabled={conversationEntries.length === 0 || conversationLoading}
                    className="flex items-center gap-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 px-3 py-1.5 rounded-lg shadow-sm transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    Copy
                  </button>
                  <button
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 transition-colors font-bold"
                    onClick={handleCloseConversationModal}
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto flex-1 bg-slate-50/50">
                {conversationLoading && (
                  <div className="flex justify-center p-8">
                    <span className="loading-spinner text-primary-500">Loading conversation...</span>
                  </div>
                )}
                {!conversationLoading && conversationError && (
                  <p className="text-center text-red-500 bg-red-50 p-4 rounded-xl border border-red-100">{conversationError}</p>
                )}
                {!conversationLoading && !conversationError && conversationEntries.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    No transcript available for this session.
                  </div>
                )}
                {!conversationLoading && conversationEntries.length > 0 && (
                  <div className="space-y-6">
                    {conversationEntries.map((entry, idx) => {
                      // Parse metadata
                      let meta = entry.metadata || entry.analysis;
                      if (typeof meta === 'string') { try { meta = JSON.parse(meta); } catch (e) { } }
                      const timestampDate = entry.timestamp ? new Date(entry.timestamp) : null;
                      const timeStr = timestampDate ? timestampDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "";

                      return (
                        <div key={idx} className="space-y-3">
                          {/* Separator with Timestamp */}
                          <div className="flex items-center gap-4">
                            <div className="h-px bg-slate-200 flex-1"></div>
                            <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-1 rounded-md">{timeStr || `Turn ${idx + 1}`}</span>
                            <div className="h-px bg-slate-200 flex-1"></div>
                          </div>

                          {/* Patient Message - renders FIRST (patient always speaks first in each turn per parser grouping logic) */}
                          {entry.patient && (
                            <div className="flex justify-start pr-12">
                              <div className="bg-white border border-slate-200 text-slate-800 rounded-2xl rounded-tl-sm px-5 py-3 shadow-sm max-w-[85%]">
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{entry.patient}</p>
                                {/* Mood Indicator */}
                                {meta?.mood && (
                                  <div className="mt-2 flex gap-2">
                                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Mood:</span>
                                    <span className="text-[10px] font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-2 py-0.5 rounded-full">{meta.mood}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Doctor Message - renders SECOND (doctor responds after patient) */}
                          {entry.doctor && (
                            <div className="flex justify-end pl-12">
                              <div className="bg-white dark:bg-secondary-800 border-2 border-primary-500 dark:border-primary-400 text-primary-700 dark:text-primary-300 rounded-2xl rounded-tr-sm px-5 py-3 shadow-md max-w-[85%]">
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{entry.doctor}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="p-4 border-t bg-white flex justify-end" style={{ display: 'none' }}>
                <Button
                  onClick={handleDownloadConversationPdf}
                  disabled={conversationLoading}
                  className="flex gap-2 items-center bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {conversationLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      Download PDF Transcript
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

